<template>
  <v-app>
    <v-main>
      <div id="app" class="app">
        <router-view />
      </div>
    </v-main>
  </v-app>
</template>

<script setup lang="ts">
import { onMounted, provide } from "vue";
import AuthorizationInterceptor from "../Authentication/services/authorization-interceptor";
import { Highlighter } from "../Editor/services/highlighter";
import RappidContext from "../Editor/rappid/rappid-context";
import { DirtyState } from "../Editor/services/dirty-state";
import GeneratorModelState from "../Editor/services/generation/generator-model-state";
import { GeneratorService } from "../Editor/services/generation/generator-service";
import ModelLoader from "../Editor/services/model-loader";
import LocalStorage from "../Editor/services/persistance/local-storage";
import { PlayerService } from "../Editor/services/player/player-service";
import { CodeSimulationService } from "../Editor/services/simulation/code/simulation-service-code";
import { StatechartsService } from "../Editor/services/statecharts/statecharts-service";
import { StencilService } from "../Editor/services/stencil-service";
import RecentItemsService from "../Editor/services/storage/recent-items-service";
import UserInterface from "../Editor/services/ui/user-interface";
import { ValidatorService } from "../Editor/services/validation-service";
import { TransformerService } from "../Editor/services/transformer-service";
import { GraphLayouter } from "../Editor/services/layout/graph-layouter";
import { CopilotService } from "../Editor/services/copilot/copilot-service";
import MaintenanceInterceptor from "../Maintenance/maintenance-interceptor";
import { UserService } from "../Authentication/services/user-service";
import { SubscriptionService } from "../Subscription/services/subscription-service";

import { getConfig } from "../../config";
import { GeneratorDownloadService } from "../Editor/services/generation/generator-download-service";
import { TermsAndConditionsService } from "../Editor/services/terms/terms-and-conditions-service";
import { initAvoid } from "../Editor/services/layout/avoid-loader";
import { Validator } from "../Editor/rappid/validator";

const { backendURL, languageServerURL } = getConfig();

const rappidContext = new RappidContext();
const transformerService = new TransformerService(backendURL, "api/models/xml");
const generatorModelState = new GeneratorModelState(rappidContext);
const dirtyState = new DirtyState(rappidContext, generatorModelState);
const exampleStatechartService = new StatechartsService("api/models", backendURL, "example", dirtyState);
const cloudStatechartService = new StatechartsService("/api/models", backendURL, "storage", dirtyState);
const recentItemsService = new RecentItemsService(cloudStatechartService);
const validator = new Validator(rappidContext, cloudStatechartService);
const validatorService = new ValidatorService(backendURL, validator);
const highlighter = Highlighter.getInstance();
highlighter.addContext(rappidContext);
const simulationControl = new CodeSimulationService(rappidContext, backendURL, undefined, "store", true, dirtyState);
const modelLoader = new ModelLoader(rappidContext, generatorModelState);
const generatorService = new GeneratorService(backendURL);
const playerService = new PlayerService(
  backendURL,
  validatorService,
  rappidContext,
  cloudStatechartService,
  true,
  dirtyState
);

const localStorage = new LocalStorage();
const userInterface = new UserInterface(rappidContext);
const userService = new UserService(backendURL);
new SubscriptionService(backendURL);

const authInterceptor = new AuthorizationInterceptor([
  backendURL,
  languageServerURL,
  import.meta.env.VUE_APP_STORAGE_BACKEND_URL,
]);
const maintenanceInterceptor = new MaintenanceInterceptor();
const stencilService = new StencilService();
const layoutService = new GraphLayouter();

const copilotService = new CopilotService(backendURL, JSON.parse(import.meta.env.VUE_APP_COPILOT_ENABLED));

const generatorDownloadService: GeneratorDownloadService = new GeneratorDownloadService();

const termsAndConditionsService = new TermsAndConditionsService(backendURL);

provide("rappidContext", rappidContext);
provide("modelLoader", modelLoader);
provide("validatorService", validatorService);
provide("recentItemsService", recentItemsService);
provide("simulationControl", simulationControl);
provide("highlighter", highlighter);
provide("generatorService", generatorService);
provide("playerService", playerService);
provide("generatorModelState", generatorModelState);
provide("dirtyState", dirtyState);
provide("localStorage", localStorage);
provide("userInterface", userInterface);
provide("exampleStatechartService", exampleStatechartService);
provide("cloudStatechartService", cloudStatechartService);
provide("userService", userService);
provide("stencilService", stencilService);
provide("transformerService", transformerService);
provide("layoutService", layoutService);
provide("copilotService", copilotService);
provide("generatorDownloadService", generatorDownloadService);
provide("termsAndConditionsService", termsAndConditionsService);
onMounted(async () => {
  authInterceptor.enable();
  maintenanceInterceptor.enable();
  await initAvoid(); // blockt UI bis bereit
  rappidContext.applyRouter();
});
</script>

<style src="./App.css"></style>
